import React from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { GroupedTimeSeriesWidgetProps } from './grouped_time_series_widget'
import AnalyticsDetailModal from '../analytics_details_modal'
import { NumberWidgetProps } from '@/components/shared/number_widget'
import { DrilldownWidgetType } from '@/utils/constants'
import { useClickTracker } from '@/hooks/click_tracker'

type DrilldownWidgetProps = {
  widgetProps: GroupedTimeSeriesWidgetProps<string> | NumberWidgetProps
  children: React.ReactNode
}

/**
 * DrilldownWidget - A component that renders either a GroupedTimeSeriesWidget or a RankedListWidget
 * and provides drilldown functionality using the provided query parameters.
 *
 * @param widgetProps - The props for the widget (either GroupedTimeSeriesWidgetProps or RankedListWidgetProps)
 * @param queryParams - The query parameters to use for the drilldown query
 */

// Helper function to determine if the widget is a GroupedTimeSeriesWidget or NumberWidget
function isGroupedTimeSeriesWidget(
  props: GroupedTimeSeriesWidgetProps<string> | NumberWidgetProps,
): props is GroupedTimeSeriesWidgetProps<string> {
  return 'chartData' in props
}

function isNumberWidget(
  props: GroupedTimeSeriesWidgetProps<string> | NumberWidgetProps,
): props is NumberWidgetProps {
  return 'drilldownData' in props
}

function getDrilldownData(
  props: GroupedTimeSeriesWidgetProps<string> | NumberWidgetProps,
) {
  if (isNumberWidget(props)) {
    return props.drilldownData
  } else if (isGroupedTimeSeriesWidget(props)) {
    return props.chartData
  }
}

const DrilldownWidget: React.FC<DrilldownWidgetProps> = ({ widgetProps, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const clickTracker = useClickTracker()

  const alertData = getDrilldownData(widgetProps)
  const hasAlertData = alertData && alertData.length > 0

  const handleClick = () => {
    if (hasAlertData) {
      if (widgetProps.clickTrackingEventName) {
        clickTracker(widgetProps.clickTrackingEventName)
      }
      onOpen()
    }
  }

  return (
    <>
      {React.cloneElement(children as React.ReactElement, {
        ...(hasAlertData ? { onClick: handleClick } : {}),
        ...(children as React.ReactElement).props,
      })}

      <AnalyticsDetailModal
        alertData={alertData}
        chartData={isGroupedTimeSeriesWidget(widgetProps) ? widgetProps : null}
        helperText={widgetProps.helperText}
        isOpen={isOpen}
        onClose={onClose}
        title={widgetProps.title}
        widgetType={
          isGroupedTimeSeriesWidget(widgetProps)
            ? DrilldownWidgetType.GROUPED_TIME_SERIES
            : isNumberWidget(widgetProps)
            ? DrilldownWidgetType.NUMBER
            : null
        }
      />
    </>
  )
}

export default DrilldownWidget
